import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { useAuth } from "@/context/AuthContext";
import { getAllTemplates, getFavouriteDrugs, getOwnClinicalTerms } from "@/lib/api";
import TemplatesTab from "@/components/Presets/TemplatesTab";
import MyMedicines from "@/components/Presets/MyMedicines";
import OwnEntries from "@/components/Presets/OwnEntries";
import { BookOpen, Plus } from "lucide-react";

const TABS = [
  { id: "templates",  label: "Templates" },
  { id: "medicines",  label: "Medicines",  doctor: true },
  { id: "complaints", label: "Complaints", doctor: true },
  { id: "diagnoses",  label: "Diagnoses",  doctor: true },
  { id: "tests",      label: "Tests",      doctor: true },
] as const;

type TabId = typeof TABS[number]["id"];

const TRIGGER = "flex-shrink-0 gap-1.5 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:font-semibold px-0 py-2.5 touch:py-3 text-sm";

/**
 * Everything saved for the doctor's prescriptions: templates, favourite medicines with their usual dose,
 * and the complaints, diagnoses and tests they wrote that aren't in the standard list. Medicines and own
 * entries belong to a doctor, so only doctors see those tabs.
 */
export default function PresetsPage() {
  const { currentUser } = useAuth();
  const isDoctor = !!currentUser?.doctor_id;
  const tabs = TABS.filter(t => !("doctor" in t) || isDoctor);

  // The tab lives in the URL (?tab=medicines) so help and other screens can link straight to it
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (tabs.some(t => t.id === searchParams.get("tab")) ? searchParams.get("tab") : "templates") as TabId;
  const [newTemplate, setNewTemplate] = useState(0);

  // How many each tab holds, so the doctor sees where there's something to tidy (own entries: in use only)
  const [counts, setCounts] = useState<Partial<Record<TabId, number>>>({});
  const refreshCounts = useCallback(async () => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    const active = (r: any[] | null) => (r ?? []).filter(e => e.active).length;
    const [templates, medicines, complaints, diagnoses, tests] = await Promise.allSettled([
      getAllTemplates(currentUser.doctor_id, currentUser.clinic_id).then((d: any) => d?.templates?.length ?? 0),
      getFavouriteDrugs().then(d => d.length),
      getOwnClinicalTerms("complaint").then(active),
      getOwnClinicalTerms("diagnosis").then(active),
      getOwnClinicalTerms("test").then(active),
    ]);
    const value = (r: PromiseSettledResult<number>) => (r.status === "fulfilled" ? r.value : undefined);
    setCounts({ templates: value(templates), medicines: value(medicines), complaints: value(complaints), diagnoses: value(diagnoses), tests: value(tests) });
  }, [currentUser?.doctor_id, currentUser?.clinic_id]);

  useEffect(() => { refreshCounts(); }, [refreshCounts]);
  useEffect(() => { document.title = "Presets | OneCheckUp"; }, []);

  return (
    <div className="prescription-form-container">
      <PageHeader
        icon={BookOpen}
        title="Presets"
        subtitle="Templates, medicines and your own entries"
        className="mb-3"
        actions={tab === "templates" && (
          <Button size="sm" className="gap-1.5" onClick={() => setNewTemplate(n => n + 1)}>
            <Plus className="w-4 h-4" />New template
          </Button>
        )}
      />
      <Tabs value={tab} onValueChange={v => setSearchParams({ tab: v }, { replace: true })}>
        <TabsList className="flex w-full justify-start gap-5 sm:gap-6 overflow-x-auto bg-transparent rounded-none h-auto p-0 border-b border-border [scrollbar-width:none]">
          {tabs.map(t => (
            <TabsTrigger key={t.id} value={t.id} className={TRIGGER}>
              {t.label}
              {counts[t.id] != null && <span className="font-mono text-xs font-normal tabular-nums text-muted-foreground">{counts[t.id]}</span>}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="templates" className="mt-4"><TemplatesTab newRequest={newTemplate} onChanged={refreshCounts} /></TabsContent>
        {isDoctor && (<>
          <TabsContent value="medicines" className="mt-4"><MyMedicines onChanged={refreshCounts} /></TabsContent>
          <TabsContent value="complaints" className="mt-4"><OwnEntries kind="complaint" onChanged={refreshCounts} /></TabsContent>
          <TabsContent value="diagnoses" className="mt-4"><OwnEntries kind="diagnosis" onChanged={refreshCounts} /></TabsContent>
          <TabsContent value="tests" className="mt-4"><OwnEntries kind="test" onChanged={refreshCounts} /></TabsContent>
        </>)}
      </Tabs>
    </div>
  );
}
